// Settings > Forms: the catalogue, each form's chronology and the packages
// that may need rebuilding. Staff read; admins check, re-download and decide.

import { useEffect, useRef, useState } from "react";
import type {
  AffectedPackageRow,
  FormCatalogueRow,
  FormEditionRow,
  FormFillCheck,
} from "@duralex/shared";
import { useCurrentUser } from "../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { Skeleton, SkeletonGroup } from "../../components/chrome/Skeleton";
import { Dialog } from "../../components/chrome/Dialog";
import { EntityLink } from "../../components/chrome/EntityLink";
import { EMPTY } from "../../components/chrome/SepList";
import { useFirmStaff } from "../../hooks/useFirmStaff";
import {
  useAffectedPackages,
  useFormActions,
  useFormDecision,
  useFormEditions,
  useForms,
} from "../../hooks/useForms";
import { downloadFormPdf, fetchFormBlob } from "../../api/forms";
import { fmtDate, fmtDay } from "../../lib/dates";
import { SettingsNav } from "./SettingsNav";
import { EditionChanges } from "./EditionChanges";

// "other" is any edition but the one in use, older or not: the word must
// not promise more than the comparison knows.
const VERDICT: Record<AffectedPackageRow["verdict"], { word: string; tone: "warn" | "muted" }> = {
  other: { word: "Another edition", tone: "warn" },
  before: { word: "Built before the current edition", tone: "warn" },
  unknown: { word: "Edition unknown", tone: "muted" },
};

const COLUMNS = [
  "Form",
  "Edition in use",
  "Published by USCIS",
  "File dated",
  "Seen by DuraLex",
  "Downloaded",
  "Checked",
  "Can be filled",
];

type Status = { text: string; bad?: boolean } | null;

function day(value: string | null | undefined): string {
  return value ? fmtDay(value) : EMPTY;
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function names(formIds: string[]): string {
  return formIds.map((id) => id.toUpperCase()).join(", ");
}

/** Words for assistive tech alone, so a control reads as its own: Open I-485, not Open. */
function ForAt({ words }: { words: string }) {
  return <span className="sr-only">{words}</span>;
}

export function FormsSettings() {
  const user = useCurrentUser();
  const isAdmin = isAdminRole(user?.role);
  const formsQ = useForms();
  const actions = useFormActions();
  const [status, setStatus] = useState<Status>(null);
  // A row's own outcome sits in its row: the card's line is off screen once
  // the admin has scrolled to the row's buttons or down to the last rows.
  const [notes, setNotes] = useState<Record<string, Status>>({});
  const note = (formId: string, value: Status) =>
    setNotes((prev) => ({ ...prev, [formId]: value }));
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const [preview, setPreview] = useState<{ title: string; url: string } | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const previewUrl = useRef<string | null>(null);

  // A blob URL lives until the dialog closes - or until the page goes.
  useEffect(() => {
    const held = previewUrl;
    return () => {
      if (held.current) URL.revokeObjectURL(held.current);
    };
  }, []);

  function dropPreviewUrl() {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = null;
  }

  function closePreview() {
    dropPreviewUrl();
    setPreview(null);
  }

  async function openForm(row: FormCatalogueRow) {
    setOpening(row.formId);
    try {
      const url = URL.createObjectURL(await fetchFormBlob(row.formId));
      dropPreviewUrl();
      previewUrl.current = url;
      setPreview({ title: `${row.formId.toUpperCase()} - ${row.version ?? "Not read yet"}`, url });
      note(row.formId, null);
    } catch (err) {
      note(row.formId, { text: errorText(err), bad: true });
    } finally {
      setOpening(null);
    }
  }

  function toggle(formId: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(formId)) next.delete(formId);
      else next.add(formId);
      return next;
    });
  }

  // One check at a time: a second one started meanwhile would take the
  // first one's outcome with it (the mutation answers its latest caller).
  const checking = actions.checkOne.isPending || actions.checkAll.isPending;

  function checkOne(formId: string) {
    actions.checkOne.mutate(formId, {
      onSuccess: (r) => {
        // The reason is the Checked cell's to tell, from the reread catalogue;
        // the note says the check ran, in case the cell reads as before.
        if (r.status === "failed") note(formId, { text: "Check failed", bad: true });
        else if (r.status === "updated") {
          note(formId, { text: `Checked - updated to ${r.newVersion ?? "a new file"}` });
        } else note(formId, { text: "Checked - no change" });
      },
      onError: (err) => note(formId, { text: errorText(err), bad: true }),
    });
  }

  function checkAll() {
    actions.checkAll.mutate(undefined, {
      onSuccess: (r) => {
        // Every row was just checked: a note from an earlier check is stale.
        setNotes({});
        const updated = r.results.filter((x) => x.status === "updated").map((x) => x.formId);
        const failed = r.results.filter((x) => x.status === "failed").map((x) => x.formId);
        const changes = updated.length
          ? `${updated.length} updated (${names(updated)})`
          : "no changes";
        const failures = failed.length ? `, ${failed.length} failed (${names(failed)})` : "";
        setStatus({
          text: `Checked ${r.checked} forms - ${changes}${failures}`,
          bad: failed.length > 0,
        });
      },
      onError: (err) => setStatus({ text: errorText(err), bad: true }),
    });
  }

  function redownload() {
    actions.redownload.mutate(undefined, {
      onSuccess: () =>
        setStatus({
          text: "Started - the files are fetched in the background; refresh in a minute.",
        }),
      onError: (err) => setStatus({ text: errorText(err), bad: true }),
    });
  }

  const rows = formsQ.data ?? [];

  return (
    <main className="cc-main">
      <div className="cc-body settings-body settings-wide">
        <SettingsNav />
        <header className="settings-head">
          <div>
            <h2>Forms</h2>
            <p className="settings-sub">
              The federal forms DuraLex fills, each with the edition its file prints. Published is
              the day USCIS announced it, File dated is the file's own date, Seen by DuraLex is the
              day this office first held it - three different facts.
            </p>
          </div>
        </header>

        <section className="cc-card settings-log settings-forms">
          <div className="cc-card-head">
            <div>
              <div className="cc-card-title">Catalogue</div>
              <div className="cc-card-sub">
                {formsQ.data
                  ? `${rows.length} forms`
                  : formsQ.isError
                    ? "Not read"
                    : "Reading the catalogue"}
              </div>
            </div>
            {isAdmin && (
              <div className="actions">
                <button
                  type="button"
                  className="cc-link-btn"
                  disabled={checking}
                  onClick={checkAll}
                >
                  {actions.checkAll.isPending ? "Checking..." : "Check all"}
                </button>
                <button
                  type="button"
                  className="cc-link-btn"
                  disabled={actions.redownload.isPending}
                  onClick={redownload}
                >
                  Download all again
                </button>
              </div>
            )}
          </div>

          <p className={"settings-status forms-status" + (status?.bad ? " bad" : "")} role="status">
            {status?.text ?? ""}
          </p>

          {formsQ.isLoading && (
            <div className="forms-status">
              <SkeletonGroup label="Reading the form catalogue">
                <Skeleton width="70%" />
                <Skeleton width="55%" />
                <Skeleton width="60%" />
              </SkeletonGroup>
            </div>
          )}

          {formsQ.isError && (
            <p className="settings-error forms-status">We could not read the form catalogue.</p>
          )}

          {formsQ.data && rows.length === 0 && (
            <div className="cc-empty">
              <div className="title">The form catalogue is empty</div>
              <div className="body">
                {isAdmin
                  ? "Download all again fetches the forms from uscis.gov and justice.gov."
                  : "An admin fetches the forms from this page."}
              </div>
            </div>
          )}

          {rows.length > 0 && (
            <div className="forms-scroll">
              <div className="forms-table" role="table" aria-label="Form catalogue">
                <div className="forms-head settings-label" role="row">
                  {COLUMNS.map((column) => (
                    <span role="columnheader" key={column}>
                      {column}
                    </span>
                  ))}
                  <span role="columnheader">
                    <ForAt words="Actions" />
                  </span>
                </div>
                {rows.map((row) => (
                  <FormRow
                    key={row.formId}
                    row={row}
                    isAdmin={isAdmin}
                    expanded={open.has(row.formId)}
                    onToggle={() => toggle(row.formId)}
                    onOpen={() => void openForm(row)}
                    opening={opening === row.formId}
                    openBusy={opening !== null}
                    onDownload={() =>
                      downloadFormPdf(row.formId).then(
                        () => note(row.formId, null),
                        (err: unknown) => note(row.formId, { text: errorText(err), bad: true }),
                      )
                    }
                    note={notes[row.formId] ?? null}
                    onCheck={() => checkOne(row.formId)}
                    checking={checking}
                    checkingThis={
                      actions.checkOne.isPending && actions.checkOne.variables === row.formId
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </section>

        <Dialog
          open={preview !== null}
          onClose={closePreview}
          title={preview?.title ?? ""}
          className="dlg-pdf"
        >
          {preview && <iframe className="forms-preview" title={preview.title} src={preview.url} />}
        </Dialog>
      </div>
    </main>
  );
}

function FormRow(props: {
  row: FormCatalogueRow;
  isAdmin: boolean;
  expanded: boolean;
  onToggle: () => void;
  onOpen: () => void;
  opening: boolean;
  openBusy: boolean;
  onDownload: () => void;
  onCheck: () => void;
  checking: boolean;
  checkingThis: boolean;
  note: Status;
}) {
  const { row, isAdmin, expanded } = props;
  const id = row.formId.toUpperCase();
  const detailsId = `form-${row.formId}-details`;
  const fillable = row.fillStrategy !== "unsupported";
  return (
    <>
      <div className="forms-row" role="row">
        <span role="rowheader">
          <button
            type="button"
            className="forms-form"
            aria-expanded={expanded}
            aria-controls={detailsId}
            onClick={props.onToggle}
          >
            <span className="name">
              {id} <span className="cc-pill muted">{row.agency}</span>
            </span>
            <span className="sub">{row.name}</span>
          </button>
        </span>
        <span role="cell" className={row.version ? undefined : "forms-muted"}>
          {row.version ?? "Not read yet"}
        </span>
        <span role="cell" className="tnum">
          {day(row.edition?.publishedAt)}
        </span>
        <span role="cell" className="tnum">
          {fmtDate(row.edition?.fileDatedAt, EMPTY)}
        </span>
        <span role="cell" className="tnum">
          {row.edition ? fmtDate(row.edition.firstSeenAt, "Before tracking") : EMPTY}
        </span>
        <span role="cell" className="tnum">
          {fmtDate(row.lastDownloadedAt, EMPTY)}
        </span>
        <span role="cell" className="tnum">
          {fmtDate(row.lastCheckedAt, EMPTY)}
          {row.lastCheckError && (
            <span className="settings-status forms-note bad">Failed: {row.lastCheckError}</span>
          )}
        </span>
        <span role="cell">
          <span className={"cc-pill " + (fillable ? "ok" : "muted")}>
            {fillable ? "Yes" : "Not yet"}
          </span>
        </span>
        <span role="cell" className="forms-actions">
          <span className="forms-buttons">
            <a className="cc-link-btn" href={row.pageUrl} target="_blank" rel="noreferrer">
              {row.agency === "EOIR" ? "justice.gov" : "uscis.gov"}
              <ForAt words={` ${id}, opens in a new tab`} />
            </a>
            <button
              type="button"
              className="cc-link-btn"
              disabled={props.openBusy}
              onClick={props.onOpen}
            >
              {props.opening ? "Opening..." : "Open"}
              <ForAt words={` ${id}`} />
            </button>
            <button type="button" className="cc-link-btn" onClick={props.onDownload}>
              Download
              <ForAt words={` ${id}`} />
            </button>
            {isAdmin && (
              <button
                type="button"
                className="cc-link-btn"
                disabled={props.checking}
                onClick={props.onCheck}
              >
                {props.checkingThis ? "Checking..." : "Check now"}
                <ForAt words={` ${id}`} />
              </button>
            )}
          </span>
          <span
            className={"settings-status forms-note" + (props.note?.bad ? " bad" : "")}
            role="status"
          >
            {props.note?.text ?? ""}
          </span>
        </span>
      </div>
      {expanded && (
        <div role="row">
          <div role="cell" aria-colspan={9} className="forms-details" id={detailsId}>
            <FormDetails formId={row.formId} isAdmin={isAdmin} />
          </div>
        </div>
      )}
    </>
  );
}

/** What one fill with no data found; nothing while the form is not fillable or the fill passed. */
function FillCheckNote({ check }: { check: FormFillCheck | null }) {
  if (!check) return null;
  if (check.error) {
    return (
      <>
        <span className="cc-pill muted">Fill check failed</span>
        <span className="forms-muted forms-edition-sub">Fill check: {check.error}</span>
      </>
    );
  }
  const n = check.missingFields.length;
  if (n === 0) return null;
  const names = check.missingFields.slice(0, 5).join(", ");
  const more = n > 5 ? ` and ${n - 5} more` : "";
  return (
    <>
      <span className="cc-pill warn">
        {n} {n === 1 ? "field" : "fields"} missing
      </span>
      <span className="forms-muted forms-edition-sub">
        Missing: {names}
        {more}
      </span>
    </>
  );
}

function SubHead({ columns }: { columns: string[] }) {
  return (
    <div className="forms-sub-row head settings-label" role="row">
      {columns.map((column) => (
        <span role="columnheader" key={column}>
          {column}
        </span>
      ))}
    </div>
  );
}

function FormDetails({ formId, isAdmin }: { formId: string; isAdmin: boolean }) {
  const editionsQ = useFormEditions(formId);
  const affectedQ = useAffectedPackages(formId);
  const staffQ = useFirmStaff();
  const decide = useFormDecision(formId);
  const [changes, setChanges] = useState<FormEditionRow | null>(null);
  const nameOf = (userId: string | null) =>
    (userId && staffQ.data?.find((s) => s.id === userId)?.fullName) || EMPTY;
  const packages = affectedQ.data?.packages ?? [];

  return (
    <>
      <section className="forms-editions">
        <h4 className="settings-label">Editions</h4>
        {editionsQ.isPending && <Skeleton width="60%" />}
        {editionsQ.isError && (
          <p className="settings-error" role="alert">
            Could not read the chronology.
          </p>
        )}
        {editionsQ.data && editionsQ.data.length === 0 && (
          <p className="settings-hint">No edition recorded for this form.</p>
        )}
        {editionsQ.data && editionsQ.data.length > 0 && (
          <div role="table" aria-label="Editions">
            <SubHead
              columns={[
                "Edition",
                "In use from",
                "Replaced",
                "Published",
                "File dated",
                "Grace period",
                "Changes",
              ]}
            />
            {editionsQ.data.map((e) => (
              <div className="forms-sub-row" key={e.editionKey} role="row">
                <span role="rowheader">
                  {e.label} {e.current && <span className="cc-pill ok">in use</span>}{" "}
                  <FillCheckNote check={e.fillCheck} />
                  {e.instructions?.changedAt && (
                    <span className="forms-muted forms-edition-sub">
                      Instructions updated{" "}
                      <span className="tnum">{fmtDate(e.instructions.changedAt)}</span>
                    </span>
                  )}
                </span>
                <span role="cell" className="tnum">
                  {e.firstSeenAt ? fmtDate(e.firstSeenAt) : e.baseline ? "Before tracking" : EMPTY}
                </span>
                <span role="cell" className="tnum">
                  {fmtDate(e.replacedAt, EMPTY)}
                </span>
                <span role="cell" className="tnum">
                  {day(e.publishedAt)}
                </span>
                <span role="cell" className="tnum">
                  {fmtDate(e.fileDatedAt, EMPTY)}
                </span>
                <span role="cell">
                  <DecisionCell
                    formId={formId}
                    row={e}
                    isAdmin={isAdmin}
                    decide={decide}
                    nameOf={nameOf}
                  />
                </span>
                <span role="cell">
                  <button type="button" className="cc-link-btn" onClick={() => setChanges(e)}>
                    What changed
                    <ForAt words={` ${formId.toUpperCase()} ${e.label}`} />
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="forms-affected">
        <h4 className="settings-label">
          Packages that may need rebuilding{affectedQ.data ? ` (${packages.length})` : ""}
        </h4>
        {affectedQ.isPending && <Skeleton width="60%" />}
        {affectedQ.isError && (
          <p className="settings-error" role="alert">
            Could not read the packages that may need rebuilding.
          </p>
        )}
        {affectedQ.data && packages.length === 0 && (
          <p className="settings-hint">
            {isAdmin
              ? "No ready package in an open matter may need rebuilding."
              : "No ready package in a matter you can see may need rebuilding."}
          </p>
        )}
        {packages.length > 0 && (
          <div role="table" aria-label="Packages that may need rebuilding">
            <SubHead columns={["Matter", "Built", "By", "Edition", "Why"]} />
            {packages.map((p) => (
              <div className="forms-sub-row" key={p.packageId} role="row">
                <span role="rowheader">
                  {p.caseEId ? (
                    <EntityLink kind="matter" id={p.caseId} eID={p.caseEId} className="tnum-mono">
                      {p.caseEId}
                    </EntityLink>
                  ) : (
                    <span className="forms-muted">No matter id</span>
                  )}
                </span>
                <span role="cell" className="tnum">
                  {fmtDate(p.generatedAt, EMPTY)}
                </span>
                <span role="cell">{nameOf(p.generatedBy)}</span>
                <span role="cell">{p.edition ?? "unknown"}</span>
                <span role="cell">
                  <span className={"cc-pill " + VERDICT[p.verdict].tone}>
                    {VERDICT[p.verdict].word}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <Dialog
        open={changes !== null}
        onClose={() => setChanges(null)}
        title={changes ? `${formId.toUpperCase()} - ${changes.label}` : ""}
        className="forms-changes"
      >
        {changes && <EditionChanges formId={formId} editionKey={changes.editionKey} />}
      </Dialog>
    </>
  );
}

function DecisionCell({
  formId,
  row,
  isAdmin,
  decide,
  nameOf,
}: {
  formId: string;
  row: FormEditionRow;
  isAdmin: boolean;
  decide: ReturnType<typeof useFormDecision>;
  nameOf: (userId: string | null) => string;
}) {
  // The decidedAt the admin saw when they pressed Change. The editor stays
  // open only while that is still the decision on screen, so one recorded
  // meanwhile shows itself instead of being replaced blind.
  const [replacing, setReplacing] = useState<string | null>(null);
  const [until, setUntil] = useState("");
  if (row.baseline) {
    return <span className="forms-muted">In use when tracking began - no decision needed</span>;
  }
  const recorded = row.decision?.decidedAt ? row.decision : null;
  // The same edition label can be in use on two forms, so a control's hidden
  // words carry both.
  const own = ` ${formId.toUpperCase()} ${row.label}`;
  const failed =
    decide.isError && decide.variables?.editionKey === row.editionKey
      ? errorText(decide.error)
      : null;
  const clearFailure = () => {
    if (decide.isError) decide.reset();
  };
  if (recorded && replacing !== recorded.decidedAt) {
    return (
      <span>
        <span className="tnum">
          {recorded.noGracePeriod
            ? "No grace period"
            : `Older edition accepted until ${day(recorded.olderAcceptedUntil)}`}
        </span>
        <span className="forms-muted tnum">
          {" "}
          by {nameOf(recorded.decidedBy)}, {fmtDate(recorded.decidedAt)}
        </span>
        {isAdmin && (
          <>
            {" "}
            <button
              type="button"
              className="cc-link-btn"
              onClick={() => {
                clearFailure();
                setReplacing(recorded.decidedAt);
              }}
            >
              Change
              <ForAt words={own} />
            </button>
          </>
        )}
        {failed && (
          <span className="settings-status forms-note bad" role="alert">
            {failed}
          </span>
        )}
      </span>
    );
  }
  if (!isAdmin) return <span className="forms-muted">Not decided</span>;
  const busy = decide.isPending && decide.variables?.editionKey === row.editionKey;
  const record = (body: { olderAcceptedUntil: string } | { noGracePeriod: true }) =>
    decide.mutate(
      {
        editionKey: row.editionKey,
        body: { ...body, ...(replacing ? { replaces: replacing } : {}) },
      },
      {
        onSuccess: () => {
          setReplacing(null);
          setUntil("");
        },
      },
    );
  return (
    <form
      className="forms-decision"
      onSubmit={(e) => {
        e.preventDefault();
        if (until) record({ olderAcceptedUntil: until });
      }}
    >
      <input
        type="date"
        className="cc-field"
        aria-label={`Older edition accepted until,${own}`}
        value={until}
        disabled={busy}
        onChange={(e) => setUntil(e.target.value)}
      />
      <button type="submit" className="cc-link-btn" disabled={busy || !until}>
        Record
        <ForAt words={own} />
      </button>
      <button
        type="button"
        className="cc-link-btn"
        disabled={busy}
        onClick={() => record({ noGracePeriod: true })}
      >
        No grace period
        <ForAt words={own} />
      </button>
      {recorded && (
        <button
          type="button"
          className="cc-link-btn"
          disabled={busy}
          onClick={() => {
            clearFailure();
            setReplacing(null);
          }}
        >
          Keep
          <ForAt words={own} />
        </button>
      )}
      {failed && (
        <span className="settings-status forms-note bad" role="alert">
          {failed}
        </span>
      )}
    </form>
  );
}
